/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import { getColor, pd } from '@airasia-phoenix/react-native/utils';
import React, { useMemo, useState } from 'react';
import countryList from '../../utils/countryList.json';
import { CountryData, filterAndMapCountries } from '../../utils/mapCountries';
import { withPlusDialingCode } from '../../utils/phoneUtils';

export type DialingCodeDropdownFieldViewProps = {
  label: string;
  dialingCode: string;
  countrySearch: string;
  setCountrySearch: (search: string) => void;
  onDialingCodeChange: (dialingCode: string) => void;
  disabled?: boolean;
  isError?: boolean;
  errorMessage?: string;
  testID?: string;
};

// Same inline list as UpdatePhoneView on desktop web: up to three rows.
const DROPDOWN_ITEM_HEIGHT = 56;
const getDropdownHeight = (itemCount: number) =>
  Math.min(
    Math.max(itemCount * DROPDOWN_ITEM_HEIGHT, DROPDOWN_ITEM_HEIGHT),
    DROPDOWN_ITEM_HEIGHT * 3,
  );

const endIcon = {
  icon: pd.icon.main.navigation.arrow_drop_down,
  height: 24,
  width: 24,
  tint: getColor(pd.sys.colour.content_secondary),
};

/**
 * Dialing code field for desktop web: the country list opens inline under the
 * field instead of the bottom sheet MobileFieldView uses, which does not fit
 * inside a desktop dialog.
 */
export function DialingCodeDropdownFieldView({
  label,
  dialingCode,
  countrySearch,
  setCountrySearch,
  onDialingCodeChange,
  disabled = false,
  isError,
  errorMessage,
  testID,
}: DialingCodeDropdownFieldViewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dialingCodeDisplay = withPlusDialingCode(dialingCode);

  const countryDropdownData = useMemo(() => {
    const selectedCode = dialingCode.replace('+', '');
    const checkmark = {
      iconViewProps: {
        icon: pd.icon.main.navigation.check,
        height: 24,
        width: 24,
        tint: getColor(pd.sys.colour.sentiment_success),
      },
    };
    return filterAndMapCountries(
      countryList.countries as CountryData[],
      countrySearch,
    ).map((item) => ({
      primaryText: item.primaryText,
      endElement:
        item.model.dialingCode === selectedCode ? checkmark : undefined,
      model: item.model,
    }));
  }, [dialingCode, countrySearch]);

  const toggleDropdown = (open: boolean) => {
    setIsOpen(open);
    setCountrySearch('');
  };

  return (
    <TextFieldView
      testID={testID}
      label={label}
      value={isOpen ? countrySearch : dialingCodeDisplay}
      disabled={disabled}
      editable={true}
      isError={isError}
      errorMessage={errorMessage}
      endIcon={endIcon}
      onEndIconPress={() => toggleDropdown(!isOpen)}
      // The field can stay focused after a pick, so a click must open the list
      // too, not only a focus change.
      onPress={() => {
        if (!isOpen) toggleDropdown(true);
      }}
      isFocused={(focused) => {
        if (focused && !isOpen) toggleDropdown(true);
      }}
      onChangeText={(text) => {
        if (isOpen) {
          setCountrySearch(text);
          return;
        }
        // Typing into the closed field starts a search, not "+60…".
        setIsOpen(true);
        setCountrySearch(
          text.startsWith(dialingCodeDisplay)
            ? text.slice(dialingCodeDisplay.length)
            : text,
        );
      }}
      showDropDownView={isOpen}
      dropDownViewData={countryDropdownData}
      dropDownViewHeight={getDropdownHeight(countryDropdownData.length)}
      dropDownPosition={1}
      onDropDownViewItemPress={(item: {
        primaryText?: string;
        model?: CountryData;
      }) => {
        const code = item.model?.dialingCode;
        if (code) onDialingCodeChange(code);
        toggleDropdown(false);
      }}
    />
  );
}
